import { Injectable }    from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';

import {
  lastValueFrom,
  Subject
} from 'rxjs';

import { UIRouter } from '@uirouter/core';

import {
  clearDatadogRumUser,
  setDatadogRumUser
} from '../../app/datadog-rum.init';

import { API }                            from '../api/api.constant';
import { CacheService }                   from '../api/cache.service';
import { CompanyCountryValidationsModel } from '../companies/models/company-country-validations';
import { CountryTypesModel }              from './models/session.country-types';
import { DomainParserService }            from '../core/services/domain.parser.service';
import { JsonService }                    from '../core/services/json.service';
import { SessionCompanyModel }            from './models/session.company';
import { SessionCountrySettingsModel }    from './models/session.country-settings';
import { SessionModel }                   from './models/session';
import { SessionSystemSettingsModel }     from './models/session.system-settings';
import { SessionWarningModel }            from './models/session.warning';
import { STATUSCODES }                    from '../api/api.status-codes.constant';
import { UserModel }                      from '../users/typings/user';
import { UserSettingsModel }              from '../users/typings/user.settings';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private _sessionStorageKey    = 'session_object'
  private sessionRefreshSubject = new Subject<SessionModel>();

  public sessionRefresh$ = this.sessionRefreshSubject.asObservable();

  _API         = API;
  _STATUSCODES = STATUSCODES;

  groupRights ?: Array<string>;
  session     ?: SessionModel = this.base();

  constructor (
    private cacheService       : CacheService,
    private cookieService      : CookieService,
    private domainParserService: DomainParserService,
    private httpClient         : HttpClient,
    private jsonService        : JsonService,
    private router             : UIRouter
  ) {
    window['session'](this.base());

    this.groupRights = this.get('group_rights');
  }

  base () : SessionModel {
    if (this.session) {
      return this.session;
    }

    return this.get(this._sessionStorageKey)
      ? this.jsonService.parse(this.get(this._sessionStorageKey))
      : {};
  }

  clear () : void {
    sessionStorage.clear();

    this.deleteCookie();

    clearDatadogRumUser();
  }

  clearSession () : void {
    this.setKey(this._sessionStorageKey, null);

    this.deleteCookie();
  }

  company () : SessionCompanyModel {
    return this.base().company_settings;
  }

  countrySettings () : SessionCountrySettingsModel {
    return this.base().country_settings;
  }

  countryValidation () : CompanyCountryValidationsModel {
    return this.base().country_validations;
  }

  currency () : any {
    const session = this.base();

    return {
      decimal_mark         : session.country_settings.decimal_mark        || '.',
      discount_includes_tax: session.company_settings.company_settings.discount_includes_tax,
      no_currency_fractions: session.country_settings.no_currency_fractions,
      price_includes_tax   : session.company_settings.price_includes_tax,
      purchases_tax_rate   : session.company_settings.purchases_tax_rate,
      round_total          : session.company_settings.round_total,
      sales_tax_rate       : session.company_settings.sales_tax_rate,
      symbol               : session.country_settings.currency            || '$',
      tax_name             : session.company_settings.tax_name ? session.company_settings.tax_name: 'GST',
      thousands_separator  : session.country_settings.thousands_separator || ',',
      validation           : session.country_validations.currency?.validation || '^\\s*(\\-|\\+)?(\\d+|(\\d*(\\.\\d*)))\\s*$'
    };
  }

  dealer_superuser () {
    return this.cookieService.get('dealer_superuser');
  }

  deleteCookie () {
    this.cookieService.delete('puppetUser');

    this.cookieService.delete('session_id',
      undefined,
      this.domainParserService.getHostname(),
      true
    );

    this.cookieService.delete('dealer_superuser');
  }

  get ( key : string ) : any {
    const val = sessionStorage[key];

    return this.jsonService.parse(val);
  }

  getAndSetRights () : Promise<any> {
    return lastValueFrom(this.httpClient.get(`${ this._API.url }/system/user_group_rights`, { withCredentials: true }))
    .then(( rights : any ) => {
      const groupRights : Array<string> = [];

      rights.group_rights_attributes.forEach(right => groupRights.push(right.object_name));

      this.setKey('group_rights', groupRights);

      this.groupRights = groupRights;

      return groupRights;
    });
  }

  getCountryTypes () : CountryTypesModel {
    return this.base().country_types;
  }

  getDistanceLabel ( type = 'full' ) : string {
    const countryTypes = this.getCountryTypes();
    const field        = type === 'short'
      ? 'distance_short_name'
      : 'distance_full_name';

    return countryTypes[field]
    || ( type === 'short' ? 'KMs' : 'Kilometers' );
  }

  getRights () : Array<string> {
    return this.groupRights;
  }

  getWarnings () : Array<SessionWarningModel> {
    return this.base().warnings;
  }

  handleSessionLogin ( session : SessionModel, user : UserModel = { username: session.username } ) : Promise<any> {
    this.setSession(session);

    if ( session.subscription_status === 'canceled' || session.subscription_status === 'trial_ended' ) {
      window.location.assign('activate.html');

      return Promise.reject();
    }

    else if ( session.subscription_status === 'past_due' ) {
      const error = new HttpErrorResponse({
        status: this._STATUSCODES.paymentRequired
      });

      return Promise.reject(error);
    }

    return this.getAndSetRights()
    .then(( rights : any ) => {
      if ( session.company_settings.dealer ) {
        window.location.assign('dealer.html');
      }
      else if ( session.company_settings.first_login ) {
        window.location.assign('workshop.html#/company?first_login=true');
      }
      else if ( session.super_user ) {
        window.location.assign('workshop.html#/admin_dashboard');
      }
      else {
        window.location.assign('workshop.html');
      }

      return Promise.resolve();
    });
  }

  hasRights ( module : string ) : boolean {
    return this.groupRights
      ? this.groupRights.indexOf(module) >= 0
      : false;
  }

  isAdminUser () : boolean {
    const session = this.base();

    return !!( session && session.admin );
  }

  isSessionExpired ( error : HttpErrorResponse ) : boolean {
    return error.status === this._STATUSCODES.expired
      && this.isSessionError(error);
  }

  isSessionError ( error : HttpErrorResponse ) : boolean {
    return error.error && error.error.type === 'X';
  }

  isSessionDeactivated ( error : HttpErrorResponse ) : boolean {
    return error.status === this._STATUSCODES.forbidden
      && this.isSessionError(error);
  }

  isSuperUser () : boolean {
    return this.base().super_user;
  }

  refresh () {
    let session : SessionModel;

    // Clear all the caches for dropdowns and such
    this.cacheService.removeAll();

    // Get the new session object
    return lastValueFrom(this.httpClient.get(`${this._API.url}/system/refresh_session`, { withCredentials: true }))
    .then(( response : SessionModel ) => {
      session = response;

      // Set the new session id and object
      this.setSession(session);

      // Now get group rights
      return this.getAndSetRights();
    })
    .then(response => {
      this.sessionRefreshSubject.next(session);

      window['session'](session);

      return session;
    });
  }

  removeKey ( key : string ) : void {
    sessionStorage.removeItem(key);
  }

  routeTo () {
    const route = this.get('savedRoute');

    this.router.stateService.transitionTo( route.name || 'app', route.params );

    this.removeKey('savedRoute');
  }

  saveRoute () : void {
    this.setKey('savedRoute', {
      name  : this.router.stateService?.current?.name,
      params: this.router.stateService?.params
    });
  }

  sessionID () : string {
    return this.jsonService.parse(this.cookieService.get('session_id'));
  }

  setCookie ( sessionId : string ) {
    // clear cookie before setting
    this.cookieService.delete('session_id', undefined, this.domainParserService.getHostname(), true);

    this.cookieService.set(
      'session_id',
      JSON.stringify(sessionId),
      undefined,
      undefined,
      this.domainParserService.getHostname(),
      true,
      'None'
    );
  }

  setDealerCookie ( superuser : any ) {
    if (superuser === 'GOD' || superuser === true) {
      this.cookieService.set('dealer_superuser', 'true');
    }
    else {
      this.cookieService.set('dealer_superuser', 'false');
    }

  }

  setKey ( name : string, data : any ) : void {

    // If it is an object or array,
    // parse it to a JSON string first
    if (typeof data === 'object') {
      data = JSON.stringify(data);
    }

    sessionStorage.setItem(name, data);
  }

  setSession ( session : SessionModel ) : Promise<SessionModel> {
    this.session = session;

    this.setKey(this._sessionStorageKey, session);
    this.setCookie(session.session_id);

    setDatadogRumUser({
      companyId     : session.company_settings?.id,
      subscriptionId: session.company_settings?.subscription_subscription_id,
      userId        : session.user_id
    });

    return Promise.resolve(session);
  }

  setSessionAndValidate ( session : SessionModel ) : Promise<any> {
    this.setSession(session);

    if (session.subscription_status === 'canceled' || session.subscription_status === 'trial_ended') {
      window.location.assign('/activate.html');

      return Promise.reject('Account Inactive');
    }

    return this.getAndSetRights();
  }

  setTemporarySession( session : any ) : void {
    this.session = session;
  }

  systemSettings () : SessionSystemSettingsModel {
    return this.base().system_settings;
  }

  user () : UserSettingsModel {
    const settings = this.base().user_settings;

    settings.id        = this.base().user_id;
    settings.superuser = this.base().super_user;

    return settings;
  }

  validate () : Promise<any> {
    if (this.base().subscription_status === 'canceled') {
      return Promise.reject();
    }

    return lastValueFrom(this.httpClient.get(`${this._API.url}/sessions`, { withCredentials: true }));
  }
}